import { useEffect, useRef } from 'react';
import {
  TextField,
  ToggleButton,
  FormField,
  formFieldRequiredLabel as requiredLabel,
  FlexColumn,
  PasswordField,
} from '@elementary/common-ui/components';
import { ToggleButtonGroup } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { SnowflakeAuthMethod } from 'shared/types';
import { getSnowflakeAuthMethod } from './utils';

const PRIVATE_KEY_PLACEHOLDER = `-----BEGIN PRIVATE KEY-----
...YOUR PRIVATE KEY...
-----END PRIVATE KEY-----`;

const KeyPairAuth = ({ disabled }: { disabled?: boolean }) => {
  const methods = useFormContext();
  const {
    register,
    formState: { dirtyFields, errors },
  } = methods;

  return (
    <>
      <FormField
        title="User"
        errorMessage={errors.snowflakeUser?.message as string}
      >
        <TextField
          {...register('snowflakeUser', {
            required: requiredLabel,
          })}
          variant="outlined"
          placeholder="e.g. elementary_user"
          disabled={disabled}
          size="large"
        />
      </FormField>
      <FormField
        title="Private key"
        errorMessage={errors.snowflakePrivateKey?.message as string}
      >
        <Controller
          control={methods.control}
          name="snowflakePrivateKey"
          rules={{ required: requiredLabel }}
          render={({ field }) => (
            <TextField
              {...field}
              placeholder={PRIVATE_KEY_PLACEHOLDER}
              multiline
              maxRows={5}
              disabled={disabled}
              inputProps={{
                sx: {
                  fontFamily: "'Geist Mono', monospace",
                  padding: '16.5px 0',
                },
              }}
              value={
                methods.getValues('snowflakePrivateKey') === true
                  ? '••••'
                  : methods.getValues('snowflakePrivateKey')
              }
              onChange={(e) => {
                field.onChange(e.target.value);
              }}
              onFocus={() => {
                if (methods.getValues('snowflakePrivateKey') === true) {
                  field.onChange('');
                }
              }}
            />
          )}
        />
      </FormField>
      <FormField
        title="Private key passphrase"
        optional
        errorMessage={errors.snowflakePrivateKeyPassphrase?.message as string}
      >
        <PasswordField
          {...register('snowflakePrivateKeyPassphrase')}
          placeholder="Private key passphrase"
          isFormValueDirty={dirtyFields.snowflakePrivateKeyPassphrase}
          clearFormValue={() =>
            methods.setValue('snowflakePrivateKeyPassphrase', '')
          }
          disabled={disabled}
          size="large"
        />
      </FormField>
    </>
  );
};

const PasswordAuth = ({ disabled }: { disabled?: boolean }) => {
  const methods = useFormContext();
  const {
    register,
    formState: { dirtyFields, errors },
  } = methods;

  return (
    <>
      <FormField
        title="User"
        errorMessage={errors.snowflakeUser?.message as string}
      >
        <TextField
          {...register('snowflakeUser', {
            required: requiredLabel,
          })}
          variant="outlined"
          placeholder="e.g. elementary_user"
          disabled={disabled}
          size="large"
        />
      </FormField>
      <FormField
        title="Password"
        errorMessage={errors.snowflakePassword?.message as string}
      >
        <PasswordField
          {...register('snowflakePassword', {
            required: requiredLabel,
          })}
          isFormValueDirty={dirtyFields.snowflakePassword}
          clearFormValue={() => methods.setValue('snowflakePassword', '')}
          placeholder="Password"
          disabled={disabled}
          size="large"
        />
      </FormField>
    </>
  );
};

const SnowflakeFormContent = ({ disabled }: { disabled?: boolean }) => {
  const selectedAuthMethodRef = useRef<any>();
  const methods = useFormContext();
  const {
    register,
    setValue,
    formState: { errors },
  } = methods;
  const [explicitAuthMethod, snowflakePassword] = useWatch({
    name: ['snowflakeAuthMethod', 'snowflakePassword'],
  });
  const snowflakeAuthMethod = getSnowflakeAuthMethod({
    snowflakeAuthMethod: explicitAuthMethod,
    snowflakePassword,
  });

  useEffect(() => {
    if (snowflakeAuthMethod != null) {
      selectedAuthMethodRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [snowflakeAuthMethod, selectedAuthMethodRef]);

  return (
    <>
      <FormField
        title="Account"
        errorMessage={errors.snowflakeAccount?.message as string}
      >
        <TextField
          {...register('snowflakeAccount', {
            required: requiredLabel,
          })}
          variant="outlined"
          placeholder="e.g. example.us-east-1"
          disabled={disabled}
          size="large"
        />
      </FormField>

      <FormField
        title="Warehouse"
        errorMessage={errors.snowflakeWarehouse?.message as string}
      >
        <TextField
          {...register('snowflakeWarehouse', {
            required: requiredLabel,
          })}
          variant="outlined"
          placeholder="e.g. ELEMENTARY_WAREHOUSE"
          disabled={disabled}
          size="large"
        />
      </FormField>
      <FormField
        title="User role"
        optional
        errorMessage={errors.snowflakeUserRole?.message as string}
      >
        <TextField
          {...register('snowflakeUserRole')}
          variant="outlined"
          placeholder="e.g. ELEMENTARY_ROLE"
          disabled={disabled}
          size="large"
        />
      </FormField>

      <FormField title="Select authentication method">
        <ToggleButtonGroup
          fullWidth
          value={snowflakeAuthMethod}
          onChange={(_, val: SnowflakeAuthMethod | null) => {
            if (val) {
              setValue('snowflakeAuthMethod', val);
            }
          }}
          exclusive
          disabled={disabled}
        >
          <ToggleButton value={SnowflakeAuthMethod.KEY_PAIR} size="large">
            Key pair
          </ToggleButton>
          <ToggleButton value={SnowflakeAuthMethod.PASSWORD} size="large">
            Username & password (deprecated)
          </ToggleButton>
        </ToggleButtonGroup>
      </FormField>

      <FlexColumn
        gap="inherit"
        ref={snowflakeAuthMethod != null ? selectedAuthMethodRef : undefined}
      >
        {snowflakeAuthMethod === SnowflakeAuthMethod.PASSWORD && (
          <PasswordAuth disabled={disabled} />
        )}

        {snowflakeAuthMethod === SnowflakeAuthMethod.KEY_PAIR && (
          <KeyPairAuth disabled={disabled} />
        )}
      </FlexColumn>
    </>
  );
};

export default SnowflakeFormContent;
